import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import AuditEntryItem from './AuditEntryItem';
import { useTransactionAudit } from '../../../../hooks/useTransactionAudit';

/**
 * AuditTrailComponent
 * Displays complete audit trail with timeline visualization
 * Phase 8.2 Implementation
 */
const AuditTrailComponent = ({ transactionId }) => {
  const { auditTrail, loading, error } = useTransactionAudit(transactionId);

  console.log('[AuditTrailComponent] Render - transactionId:', transactionId);
  console.log('[AuditTrailComponent] Render - loading:', loading);
  console.log('[AuditTrailComponent] Render - error:', error);
  console.log('[AuditTrailComponent] Render - auditTrail:', auditTrail);
  console.log('[AuditTrailComponent] Render - auditTrail type:', typeof auditTrail);
  console.log('[AuditTrailComponent] Render - auditTrail isArray:', Array.isArray(auditTrail));
  console.log('[AuditTrailComponent] Render - auditTrail length:', auditTrail?.length);

  if (loading) {
    console.log('[AuditTrailComponent] Showing loading spinner');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    console.log('[AuditTrailComponent] Showing error:', error);
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!auditTrail || auditTrail.length === 0) {
    console.log('[AuditTrailComponent] Showing no records message');
    console.log('[AuditTrailComponent] auditTrail is:', auditTrail);
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No audit records found for this transaction.
        </Typography>
      </Box>
    );
  }

  console.log('[AuditTrailComponent] Rendering audit trail with', auditTrail.length, 'records');

  return (
    <Box sx={{ py: 2 }}>
      {/* Header Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Transaction History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {auditTrail.length} change{auditTrail.length !== 1 ? 's' : ''} recorded
        </Typography>
      </Box>

      {/* Audit Entries Timeline View */}
      <Timeline position="alternate">
        {auditTrail.map((audit, index) => {
          const isLastItem = index === auditTrail.length - 1;

          return (
            <TimelineItem key={audit.id || index}>
              {/* Timeline Opposite Content - Time */}
              <TimelineOppositeContent color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {new Date(audit.changedAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {new Date(audit.changedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              </TimelineOppositeContent>

              {/* Timeline Separator */}
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    boxShadow: '0 4px 12px rgba(14, 107, 107, 0.15)',
                    bgcolor: '#0E6B6B',
                    border: '3px solid #fff',
                  }}
                />
                {!isLastItem && <TimelineConnector sx={{ bgcolor: '#ddd' }} />}
              </TimelineSeparator>

              {/* Timeline Content - Audit Entry */}
              <TimelineContent sx={{ px: { xs: 1, sm: 2 } }}>
                <AuditEntryItem
                  auditRecord={audit}
                  index={index + 1}
                  total={auditTrail.length}
                />
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>

      {/* Footer Info */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Showing {auditTrail.length} audit record{auditTrail.length !== 1 ? 's' : ''} • Most recent first
        </Typography>
      </Box>
    </Box>
  );
};

export default AuditTrailComponent;
